import React from 'react';
import { 
    Datagrid, 
    TextField, 
    FunctionField,
    useRecordContext,
    useGetList,
    Loading,
    ListContextProvider
} from 'react-admin';
import { Chip, Typography, Box } from '@mui/material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MailgunActivity = () => {
    const record = useRecordContext();

    // Extraire l'ID de l'utilisateur depuis l'IRI
    const utilisateurId = record?.id ? record.id.split('/').pop() : null;

    // Appel à la route personnalisée via useGetList
    const { data, isLoading, error } = useGetList(
        `utilisateurs/${utilisateurId}/mailgun_events`,
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'timestamp', order: 'DESC' }
        },
        { enabled: !!utilisateurId }
    );

    if (!record || !record.email) {
        return (
            <Box p={2}>
                <Typography>Aucun email associé à cet utilisateur</Typography>
            </Box>
        );
    }

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography color="error">
                    Erreur lors du chargement des événements Mailgun
                </Typography>
            </Box>
        );
    }

    const getEventColor = (event) => {
        switch (event) {
            case 'delivered':
                return 'success';
            case 'opened':
                return 'info';
            case 'clicked':
                return 'primary';
            case 'failed':
            case 'rejected':
                return 'error';
            case 'complained':
                return 'warning';
            default:
                return 'default';
        }
    };

    const getEventLabel = (event) => {
        const labels = {
            'delivered': 'Délivré',
            'opened': 'Ouvert',
            'clicked': 'Cliqué',
            'failed': 'Échec',
            'rejected': 'Rejeté',
            'complained': 'Plainte',
            'unsubscribed': 'Désabonné',
            'accepted': 'Accepté'
        };
        return labels[event] || event;
    };

    const events = data || [];

    if (events.length === 0) {
        return (
            <Box p={2}>
                <Typography>Aucun événement Mailgun trouvé pour {record.email}</Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Typography variant="h6" gutterBottom>
                Activité email pour {record.email}
            </Typography>
            <ListContextProvider value={{
                data: events,
                total: events.length,
                isLoading: false,
                resource: 'mailgun_events',
                sort: { field: 'timestamp', order: 'DESC' },
                setSort: () => {}
            }}>
                <Datagrid bulkActionButtons={false}>
                <FunctionField
                    label="Date"
                    render={(record) => 
                        record.timestamp 
                            ? format(new Date(record.timestamp * 1000), 'dd/MM/yyyy HH:mm', { locale: fr })
                            : '-'
                    }
                />
                <FunctionField
                    label="Événement"
                    render={(record) => (
                        <Chip 
                            label={getEventLabel(record.event)} 
                            color={getEventColor(record.event)}
                            size="small"
                        />
                    )}
                />
                <TextField source="subject" label="Sujet" />
                <TextField source="from" label="De" />
                <FunctionField
                    label="Détails"
                    render={(record) => (
                        <>
                            {record.deliveryStatus?.message && (
                                <Typography variant="caption" display="block">
                                    {record.deliveryStatus.message}
                                </Typography>
                            )}
                            {record.deliveryStatus?.['mx-host'] && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                    MX: {record.deliveryStatus['mx-host']}
                                </Typography>
                            )}
                            {record.reason && (
                                <Typography variant="caption" color="error" display="block">
                                    Raison: {record.reason}
                                </Typography>
                            )}
                            {record.messageSize && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                    Taille: {(record.messageSize / 1024).toFixed(1)} Ko
                                </Typography>
                            )}
                        </>
                    )}
                />
            </Datagrid>
            </ListContextProvider>
        </Box>
    );
};

export default MailgunActivity;
